import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Badge,
  Button,
  CircularProgress,
  IconButton,
  Popover,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ForumIcon from "@material-ui/icons/Forum";
import useSound from "use-sound";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import notifySound from "../../assets/chat_notify.mp3";
import { useSoundPreferences, effectiveVolume } from "../../utils/soundPreferences";
import ChatAvatar from "./ChatAvatar";
import {
  formatListTime,
  formatPreview,
  getChatTitle,
  lastMessageMentions,
  mergeChat,
  sortChats,
  unreadsFor,
} from "./chatUtils";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accentText = isDark ? "#8DB8F5" : "#1A4FA8";
  return {
    paper: {
      width: 380,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: 12,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
    },
    head: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1, 1.25, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headTitle: { flex: 1, fontSize: 15, fontWeight: 700 },
    markAll: {
      textTransform: "none",
      fontSize: 12,
      fontWeight: 600,
      color: accentText,
    },
    list: {
      maxHeight: 360,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      width: "100%",
      padding: theme.spacing(1.5, 2),
      border: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: "transparent",
      textAlign: "left",
      cursor: "pointer",
      fontFamily: "inherit",
      color: theme.palette.text.primary,
      "&:last-child": { borderBottom: 0 },
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    itemUnread: {
      backgroundColor: isDark ? "rgba(45,123,232,0.10)" : "#F5F8FE",
    },
    body: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    line: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    title: {
      flex: 1,
      fontSize: 14,
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    time: { fontSize: 11, color: theme.palette.text.secondary, flexShrink: 0 },
    preview: {
      flex: 1,
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    badge: {
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      boxSizing: "border-box",
      borderRadius: 10,
      backgroundColor: "#2468D4",
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    at: {
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: isDark ? "rgba(240,163,94,0.25)" : "#FDEBD7",
      color: isDark ? "#F5B27A" : "#8A3A00",
      fontSize: 12,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    strong: { fontWeight: 700 },
    strongTime: { color: accentText, fontWeight: 600 },
    strongPreview: { color: theme.palette.text.primary, fontWeight: 500 },
    empty: {
      padding: theme.spacing(4, 2),
      textAlign: "center",
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
    footer: {
      display: "flex",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    openAll: {
      flex: 1,
      textTransform: "none",
      fontWeight: 600,
      color: accentText,
      backgroundColor: isDark ? "rgba(45,123,232,0.18)" : "#E8F0FC",
      "&:hover": {
        backgroundColor: isDark ? "rgba(45,123,232,0.26)" : "#DCE8FB",
      },
    },
    newBtn: {
      textTransform: "none",
      fontWeight: 600,
      backgroundColor: "#2468D4",
      "&:hover": { backgroundColor: "#1A4FA8" },
    },
  };
});

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CHATS": {
      const next = [...state];
      (action.payload || []).forEach((chat) => {
        const index = next.findIndex((c) => c.id === chat.id);
        if (index === -1) next.push(chat);
        else next[index] = mergeChat(next[index], chat);
      });
      return next;
    }
    case "UPSERT_CHAT": {
      const chat = action.payload;
      const index = state.findIndex((c) => c.id === chat.id);
      if (index === -1) return [chat, ...state];
      const next = [...state];
      next[index] = mergeChat(state[index], chat);
      return next;
    }
    case "DELETE_CHAT":
      return state.filter((c) => c.id !== action.payload);
    case "MARK_READ":
      return state.map((c) =>
        action.payload.includes(c.id)
          ? {
              ...c,
              users: (c.users || []).map((u) =>
                u.userId === action.userId ? { ...u, unreads: 0 } : u
              ),
            }
          : c
      );
    default:
      return state;
  }
};

export default function ChatPopover() {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [chats, dispatch] = useReducer(reducer, []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Antes tocava sempre no volume máximo; agora segue o menu da conta.
  const [soundPrefs] = useSoundPreferences();
  const chatVolume = effectiveVolume(soundPrefs, "chat");
  const [play] = useSound(notifySound, { volume: chatVolume });
  const soundRef = useRef();
  soundRef.current = chatVolume > 0 ? play : () => {};

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/chats/", { params: { pageNumber } })
      .then(({ data }) => {
        if (!active) return;
        dispatch({ type: "LOAD_CHATS", payload: data.records });
        setHasMore(data.hasMore);
      })
      .catch((err) => active && toastError(err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-chat-user-${user.id}`, (data) => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPSERT_CHAT", payload: data.record });
      }
    });

    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CHAT", payload: +data.id });
        return;
      }
      const chat = data.chat;
      if (!chat) return;
      const member = (chat.users || []).some((u) => u.userId === user.id);
      if (!member) {
        dispatch({ type: "DELETE_CHAT", payload: chat.id });
        return;
      }
      dispatch({ type: "UPSERT_CHAT", payload: chat });

      if (
        data.action === "new-message" &&
        data.newMessage?.senderId !== user.id
      ) {
        // sem som se a pessoa já está lendo essa conversa
        const viewing =
          document.hasFocus() &&
          window.location.pathname === `/chats/${chat.uuid}`;
        if (!viewing && soundRef.current) soundRef.current();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user.id]);

  const sortedChats = useMemo(() => sortChats(chats), [chats]);
  const totalUnreads = useMemo(
    () => chats.reduce((sum, c) => sum + unreadsFor(c, user.id), 0),
    [chats, user.id]
  );

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      setPageNumber((p) => p + 1);
    }
  };

  const goTo = (path) => {
    setAnchorEl(null);
    history.push(path);
  };

  const markAllAsRead = () => {
    const ids = chats
      .filter((c) => unreadsFor(c, user.id) > 0)
      .map((c) => c.id);
    if (!ids.length) return;
    dispatch({ type: "MARK_READ", payload: ids, userId: user.id });
    ids.forEach((id) =>
      api.post(`/chats/${id}/read`, { userId: user.id }).catch(() => {})
    );
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <IconButton
        aria-label={
          totalUnreads > 0
            ? `Chat interno, ${totalUnreads} não lidas`
            : "Chat interno"
        }
        aria-haspopup="true"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{ color: "inherit" }}
      >
        <Badge
          color="error"
          badgeContent={totalUnreads}
          max={99}
          invisible={totalUnreads === 0}
        >
          <ForumIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.head}>
          <span className={classes.headTitle}>Chat interno</span>
          {totalUnreads > 0 && (
            <Button size="small" className={classes.markAll} onClick={markAllAsRead}>
              Marcar todas como lidas
            </Button>
          )}
        </div>

        <div className={classes.list} onScroll={handleScroll}>
          {!loading && sortedChats.length === 0 && (
            <div className={classes.empty}>Nenhuma conversa ainda.</div>
          )}
          {sortedChats.map((chat) => {
            const unreads = unreadsFor(chat, user.id);
            const strong = unreads > 0;
            return (
              <button
                type="button"
                key={chat.id}
                className={`${classes.item} ${strong ? classes.itemUnread : ""}`}
                onClick={() => goTo(`/chats/${chat.uuid}`)}
              >
                <ChatAvatar chat={chat} userId={user.id} size={40} />
                <span className={classes.body}>
                  <span className={classes.line}>
                    <span className={`${classes.title} ${strong ? classes.strong : ""}`}>
                      {getChatTitle(chat, user.id)}
                    </span>
                    <span className={`${classes.time} ${strong ? classes.strongTime : ""}`}>
                      {chat.lastMessage ? formatListTime(chat.updatedAt) : ""}
                    </span>
                  </span>
                  <span className={classes.line}>
                    <span
                      className={`${classes.preview} ${
                        strong ? classes.strongPreview : ""
                      }`}
                    >
                      {formatPreview(chat, user)}
                    </span>
                    {strong && lastMessageMentions(chat, user) && (
                      <span className={classes.at} title="Você foi mencionado">
                        @
                      </span>
                    )}
                    {strong && (
                      <span className={classes.badge}>
                        {unreads > 99 ? "99+" : unreads}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
          {loading && (
            <div className={classes.empty}>
              <CircularProgress size={20} />
            </div>
          )}
        </div>

        <div className={classes.footer}>
          <Button className={classes.openAll} onClick={() => goTo("/chats")}>
            Abrir chat interno
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.newBtn}
            startIcon={<AddIcon />}
            onClick={() => goTo("/chats?nova=1")}
          >
            Nova
          </Button>
        </div>
      </Popover>
    </div>
  );
}
